import { Schema, arrayOf, normalize } from 'normalizr';
import fetch from 'isomorphic-fetch';
import LHX from 'utils/LHX';
import {
  addUrlParam,
  checkForNestedProperty,
  readCookie,
} from 'utils/utils';

import bookJsonUtils from './bookJsonUtils';

export const CALL_API = 'CALL_API';

const dimension = new Schema('dimensions', {
  idAttribute: 'dimValId',
});

const partyRoom = new Schema('partyRooms');
const room = new Schema('rooms');
const roomPricingOption = new Schema('roomPricingOptions');
const roomRestriction = new Schema('roomRestrictions');
const transfer = new Schema('transfers');
const parkings = new Schema('parkings');
const carHires = new Schema('carHires');
const insurance = new Schema('insurance', {
  idAttribute: 'insuranceid',
});
const paymentPlan = new Schema('paymentPlans', {
  idAttribute: 'type',
});

const passenger = new Schema('passengers');
const flightMeal = new Schema('flightMeals');


dimension.define({
  children: arrayOf(dimension),
});

const selectedDimension = new Schema('dimensions', {
  idAttribute: 'id',
});

const similarHotels = new Schema('similarHotels', {
  idAttribute: 'masterId',
});

const dimensionsStructure = {
  D_Destination: arrayOf(dimension),
  D_DepartureAirport: arrayOf(dimension),
  D_BoardBasis: arrayOf(dimension),
  D_Nights: arrayOf(dimension),
  D_RoomConfiguration: arrayOf(dimension),
  D_HolidayType: arrayOf(dimension),
  D_DayOfMonth: arrayOf(dimension),
  D_Operator: arrayOf(dimension),
  D_PCM_Kids: arrayOf(dimension),
  D_PCM_Pool: arrayOf(dimension),
  D_PCM_Hotel_Facilities: arrayOf(dimension),
};

export const Schemas = {
  HOTEL_DETAILS: {
    baseState: {
      dimensions: arrayOf(selectedDimension),
    },
    dimensions: dimensionsStructure,
    similarHotels: arrayOf(similarHotels),
  },
  BASE_STATE: {
    baseState: {
      dimensions: arrayOf(selectedDimension),
    },
  },
  ANNUAL_AVAILABILITY: {
    baseState: {
      dimensions: arrayOf(selectedDimension),
    },
    dimensions: dimensionsStructure,
  },
  DIMENSIONS: {
    dimensions: dimensionsStructure,
  },
  CALENDAR_OFFERS: {
    json: {
      baseState: {
        dimensions: arrayOf(selectedDimension),
      },
    },
  },
  SEARCH_RESULTS: {
    baseState: {
      dimensions: arrayOf(selectedDimension),
    },
    dimensions: dimensionsStructure,
  },
  DAY_OF_MONTH: {
    dimensions: dimensionsStructure,
  },
  BOOK: {
    data: {
      party: arrayOf(partyRoom),
      rooms: arrayOf(room),
      room_pricing_options: arrayOf(roomPricingOption),
      roomRestrictions: arrayOf(roomRestriction),
      transfers: arrayOf(transfer),
      parkings: arrayOf(parkings),
      car_hires: arrayOf(carHires),
      insurance: arrayOf(insurance),
      payment_plans: arrayOf(paymentPlan),
      extras: {
        flightMeals: {
          options: arrayOf(flightMeal),
        },
      },
    },
    booking_info: {
      passengers: arrayOf(passenger),
      passengersByRoom: {
        0: {
          all: arrayOf(passenger),
          adults: arrayOf(passenger),
          children: arrayOf(passenger),
          infants: arrayOf(passenger),
        },
        1: {
          all: arrayOf(passenger),
          adults: arrayOf(passenger),
          children: arrayOf(passenger),
          infants: arrayOf(passenger),
        },
        2: {
          all: arrayOf(passenger),
          adults: arrayOf(passenger),
          children: arrayOf(passenger),
          infants: arrayOf(passenger),
        },
      },
    },
  },
};

/*
 * TODO: Add tests for the following methods
 *
 */

/* istanbul ignore next */
export const fetchEndpoint = (endpoint, jsonRootKey, schema, reqOptions) => {
  const requestOptions = Object.assign({ credentials: 'same-origin' }, reqOptions);

  if (requestOptions.method === 'POST' || requestOptions.method === 'PUT') {
    requestOptions.body = JSON.stringify(requestOptions.formData);
    requestOptions.headers = {
      Accept: 'application/json, text/javascript, */*',
      'Content-Type': 'application/json; charset=utf-8',
    };
  }

  return fetch(endpoint, requestOptions)
    .then(response => response.json().then(json => ({ json, response })))
    .then(({ json, response }) => {
      if (!response.ok) {
        return Promise.reject(json);
      }
      const resultJson = jsonRootKey ? json[jsonRootKey] : json;

      if (endpoint.indexOf('/book/api/') === 0) {
        bookJsonUtils.updateBookJson(resultJson);
      }

      return schema ? Object.assign({}, normalize(resultJson, schema)) : { result: resultJson };
    });
};

/* istanbul ignore next */
function chkForErrors({ endpoint, types }) {
  if (typeof endpoint !== 'string') {
    throw new Error('Specify a string endpoint URL.');
  }

  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error('Expected an array of three action types.');
  }
}

/* istanbul ignore next */
function actionWith(action, data) {
  if (data.type === 'HANDOFF_SUCCESS') {
    if (window.location.pathname.includes('book-handoff')) {
      window.location.replace(data.response.result.forward);
    } else {
      window.location.href = data.response.result.forward;
    }
  }

  if (checkForNestedProperty(data, 'response.result.status')) {
    const status = data.response.result.status;
    if (status.error && status.location) {
      window.location.href = data.response.result.status.location;
      return {
        type: 'SERVER_ERROR_REDIRECT',
      };
    }
  }

  const finalAction = Object.assign({}, action, data);
  delete finalAction[CALL_API];
  return finalAction;
}

/* istanbul ignore next */
function processApiCall({ store, action, next, callAPI }) {
  let { endpoint } = callAPI;
  const { schema, types, jsonRootKey, requestOptions, externalCallback } = callAPI;
  const [requestType, successType, failureType] = types;
  const userClass = LHX.userClass;
  const channel = readCookie('lvh_ch');

  if (typeof endpoint === 'function') {
    endpoint = endpoint(store.getState());
  }

  if (userClass) {
    endpoint = addUrlParam(endpoint, 'uc', userClass);
  }

  if (channel) {
    endpoint = addUrlParam(endpoint, 'ch', channel);
  }

  chkForErrors({ endpoint, types });
  next(actionWith(action, { type: requestType }));

  return fetchEndpoint(endpoint, jsonRootKey, schema, requestOptions)
    .then(
      (response) => {
        if (externalCallback) {
          externalCallback(response);
        }

        next(actionWith(action, {
          response,
          type: successType,
        }));
      },
      (error) => {
        if (externalCallback) {
          externalCallback(null, error);
        }

        next(actionWith(action, {
          type: failureType,
          error: error.message || 'Something bad happened',
        }));
      },
    );
}

/* istanbul ignore next */
export default store => next => (action) => {
  const callAPI = action[CALL_API];

  return (typeof callAPI === 'undefined') ?
    next(action) :
    processApiCall({ store, action, next, callAPI });
};



/** WEBPACK FOOTER **
 ** ./src/redux/middleware/api.js
 **/